/**
 * Menu centrado no cliente (plano do dossiê, 29/10/2026): Clientes no topo; as
 * visões gerais (Hoje, Publicações, Prazos e tarefas, Agenda, Casos, Leads,
 * Atendimentos), o Financeiro e as Configurações vêm abaixo, em grupos. O que cada pessoa vê
 * depende do papel, das permissões e das partes do financeiro ligadas no
 * servidor. A API continua sendo quem autoriza; o menu só não oferece o que
 * não serviria.
 */
import type { ComponentType } from 'react'
import {
  Briefcase, CalendarDays, CheckSquare, FileText, LayoutGrid, MessageSquare, Settings, Sun, TrendingUp, Users, Wallet,
} from 'lucide-react'
import type { FinanceFeature, FinanceFeatureMap } from '@/lib/finance'
import { canonicalRole, hasPermission, type OfficeRole } from '@/lib/roles'

export interface NavItem {
  label: string
  to: string
  icon?: ComponentType<{ className?: string }>
  /** Só estes papéis (canônicos) veem o item. */
  roles?: readonly OfficeRole[]
  /** Permissão `recurso:ação` exigida. */
  permission?: string
  /** Parte desligável do financeiro. */
  feature?: FinanceFeature
  /** Bloco dentro do grupo (separador visual entre blocos). */
  section?: number
}

export interface NavGroup {
  key: 'visoes' | 'financeiro' | 'configuracoes'
  label: string
  icon: ComponentType<{ className?: string }>
  items: NavItem[]
}

export interface NavigationModel {
  main: NavItem[]
  groups: NavGroup[]
}

export interface NavUser {
  role: string
  permissions?: readonly string[] | null
}

export const MAIN_NAV: readonly NavItem[] = [
  { label: 'Clientes', to: '/clients', icon: Users },
]

/** Acesso direto, sem passar pelo cliente: fica num grupo, com menos destaque. */
export const OVERVIEW_NAV: readonly NavItem[] = [
  { label: 'Hoje', to: '/hoje', icon: Sun },
  { label: 'Publicações', to: '/publications', icon: FileText },
  { label: 'Prazos e tarefas', to: '/tasks', icon: CheckSquare },
  { label: 'Agenda', to: '/calendar', icon: CalendarDays },
  { label: 'Casos', to: '/cases', icon: Briefcase },
  { label: 'Leads', to: '/leads', icon: TrendingUp },
  { label: 'Atendimentos', to: '/interactions', icon: MessageSquare },
]

export const FINANCE_PERMISSION = 'finance:read'

export const FINANCE_NAV: readonly NavItem[] = [
  { label: 'Visão simplificada', to: '/finance-v2/simplified', section: 1 },
  { label: 'Painel', to: '/finance-v2/dashboard', section: 2 },
  { label: 'Transações', to: '/finance-v2/transactions', section: 2 },
  { label: 'Transferências', to: '/finance-v2/transfers', section: 2, feature: 'transfers' },
  { label: 'Contas', to: '/finance-v2/accounts', section: 2 },
  { label: 'Categorias', to: '/finance-v2/categories', section: 2 },
  { label: 'Centros de custo', to: '/finance-v2/cost-centers', section: 2 },
  { label: 'Anuidades', to: '/finance-v2/annual-fees', section: 3 },
  { label: 'Cobrança', to: '/finance-v2/collection', section: 3 },
  { label: 'Recorrências', to: '/finance-v2/recurring', section: 3 },
  { label: 'Faturas', to: '/finance-v2/invoices', section: 4, feature: 'invoices' },
  { label: 'Itens a faturar', to: '/finance-v2/invoices/to-invoice', section: 4, feature: 'invoices' },
  { label: 'Tipos de serviço', to: '/finance-v2/service-types', section: 4, feature: 'invoices' },
  { label: 'Registro de horas', to: '/finance-v2/timesheets', section: 4, feature: 'invoices' },
  { label: 'Relatórios', to: '/finance-v2/reports', section: 5 },
  { label: 'Configuração BTG', to: '/finance-v2/btg-config', section: 5, feature: 'btg' },
  { label: 'Gerar carnê', to: '/finance-v2/carne', section: 5, feature: 'carne' },
]

export const SETTINGS_NAV: readonly NavItem[] = [
  { label: 'Equipe', to: '/settings/equipe', section: 1 },
  { label: 'Monitorados', to: '/publications/monitorados', section: 1 },
  { label: 'Importações', to: '/publications/importacoes', section: 1 },
  { label: 'Calendário forense', to: '/settings/calendario-forense', section: 1 },
  { label: 'Carteiras', to: '/settings/carteiras', section: 1 },
  { label: 'Usuários', to: '/users', section: 2, roles: ['SOCIO'] },
  { label: 'Sistema', to: '/settings', section: 2, roles: ['SOCIO'] },
  { label: 'Relatórios', to: '/reports', section: 2, roles: ['SOCIO'] },
  { label: 'Backup', to: '/backup', section: 2, permission: 'system:admin' },
]

/** Quem vê o quê: papel, permissões e partes do financeiro ligadas. */
export class NavigationPolicy {
  private readonly role: OfficeRole | null

  constructor(private readonly user: NavUser | null, private readonly features: FinanceFeatureMap) {
    this.role = canonicalRole(user?.role)
  }

  /** O agente (Hermes) não tem interface: usa só a API por chave. */
  get hasInterface(): boolean {
    return !!this.user && this.role !== 'AGENTE'
  }

  allows(item: NavItem): boolean {
    if (!this.hasInterface) return false
    if (item.roles && !item.roles.includes(this.role as OfficeRole)) return false
    if (item.permission && !hasPermission(this.user?.permissions, item.permission)) return false
    if (item.feature && !this.features[item.feature]) return false
    return true
  }

  canSeeFinance(): boolean {
    return this.hasInterface && hasPermission(this.user?.permissions, FINANCE_PERMISSION)
  }
}

export function buildNavigation(user: NavUser | null, features: FinanceFeatureMap): NavigationModel {
  const policy = new NavigationPolicy(user, features)
  const groups: NavGroup[] = []
  const overview = OVERVIEW_NAV.filter((i) => policy.allows(i))
  if (overview.length > 0) groups.push({ key: 'visoes', label: 'Visões gerais', icon: LayoutGrid, items: overview })
  if (policy.canSeeFinance()) {
    groups.push({ key: 'financeiro', label: 'Financeiro', icon: Wallet, items: FINANCE_NAV.filter((i) => policy.allows(i)) })
  }
  const settings = SETTINGS_NAV.filter((i) => policy.allows(i))
  if (settings.length > 0) groups.push({ key: 'configuracoes', label: 'Configurações', icon: Settings, items: settings })
  return { main: MAIN_NAV.filter((i) => policy.allows(i)), groups }
}

/**
 * Item ativo = o de caminho mais longo que contém a rota atual
 * (/publications/importacoes acende Importações, não Publicações).
 */
export function activeHref(pathname: string, model: NavigationModel): string | null {
  const all = [...model.main, ...model.groups.flatMap((g) => g.items)]
  let best: string | null = null
  for (const { to } of all) {
    const matches = pathname === to || pathname.startsWith(`${to}/`)
    if (matches && (!best || to.length > best.length)) best = to
  }
  return best
}

/** Itens de um grupo separados por bloco, na ordem. */
export function sectionsOf(items: readonly NavItem[]): NavItem[][] {
  const sections = new Map<number, NavItem[]>()
  for (const item of items) {
    const key = item.section ?? 0
    sections.set(key, [...(sections.get(key) ?? []), item])
  }
  return [...sections.values()]
}
